import { useEffect, useRef } from 'react'
import { Wrapper, Inner, Close, GlobalModalHeader } from './styles'
import Icon from 'components/shared/Icon'
import { closeModalActions, openModalActions } from 'helpers/helpers'
import Text from 'components/shared/Text'

interface ModalProps {
    children: React.ReactNode
    isOpened: boolean
    onClose?: () => void
    hasCloseButton?: boolean
    isCloseOnOutsideClickEnabled?: boolean
    maxWidth?: number
    isMobileFullscreen?: boolean
    hasGlobalHeader?: boolean
    headerMessage?: React.ReactNode | string
    isCenteredVerticaly?: boolean
    testIdPrefix?: string
    isBlur?: true
    isMobileCentered?: true
}

const Modal = ({
    children,
    isOpened,
    onClose,
    hasCloseButton = true,
    isCloseOnOutsideClickEnabled = true,
    maxWidth = 670,
    isMobileFullscreen = false,
    hasGlobalHeader = false,
    headerMessage,
    isCenteredVerticaly = true,
    testIdPrefix,
    isBlur,
    isMobileCentered,
}: ModalProps) => {
    const modalRef = useRef<HTMLDivElement>(null)

    const handlePressBack = () => {
        if (!isOpened) return
        onClose && onClose()
        document.documentElement.classList.remove('-modal-open')
    }

    useEffect(() => {
        window.addEventListener('popstate', handlePressBack)

        if (isOpened) {
            window.history.pushState(
                {},
                document.title,
                `${window.location.pathname}?modal=true`
            )
            openModalActions()
        }
    }, [isOpened])

    const handleClose = () => {
        onClose && onClose()
        closeModalActions(false)
        removeEventListener('popstate', handlePressBack)
    }

    const handleCloseOnOutsideClick = (event: React.MouseEvent) => {
        if (event.target !== event.currentTarget) return

        handleClose()
    }

    useEffect(() => {
        isOpened && modalRef?.current?.focus()
    }, [isOpened])

    return (
        <>
            {isOpened && (
                <Wrapper
                    isMobileCentered={isMobileCentered}
                    isBlur={isBlur}
                    onClick={(event) =>
                        isCloseOnOutsideClickEnabled &&
                        handleCloseOnOutsideClick(event)
                    }
                    isMobileFullscreen={isMobileFullscreen}
                    id="modal"
                    isCenteredVerticaly={isCenteredVerticaly}
                    tabIndex={0}
                    ref={modalRef}
                >
                    <Inner
                        maxWidth={maxWidth}
                        isMobileFullscreen={isMobileFullscreen}
                        isCenteredVerticaly={isCenteredVerticaly}
                    >
                        {hasCloseButton && !hasGlobalHeader && (
                            <Close
                                onClick={handleClose}
                                data-testid={`${
                                    testIdPrefix ? `${testIdPrefix}-` : ''
                                }button-close`}
                                tabIndex={0}
                            >
                                <Icon variant="cross" color="primary_500" />
                            </Close>
                        )}
                        {hasGlobalHeader && hasCloseButton && (
                            <GlobalModalHeader
                                pt={[16, 32]}
                                pb={[16, 32]}
                                pl={[16, 40]}
                                pr={[48, 80]}
                                isMobileFullscreen={isMobileFullscreen}
                            >
                                <Text variant="heading3">{headerMessage}</Text>

                                <Close
                                    onClick={handleClose}
                                    data-testid={`${
                                        testIdPrefix ? `${testIdPrefix}-` : ''
                                    }button-close`}
                                >
                                    <Icon variant="cross" color="primary_500" />
                                </Close>
                            </GlobalModalHeader>
                        )}
                        {children}
                    </Inner>
                </Wrapper>
            )}
        </>
    )
}

export default Modal
